import { useEffect, useState } from "react"
import { faCartShopping } from "@fortawesome/pro-solid-svg-icons"
import { faCircleInfo } from "@fortawesome/pro-thin-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, IconButton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../modules/currency/useMoney"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import { selectKitchen } from "../../../../../modules/nonAdminUser/nonAdminUserSelectors"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { WebshopType } from "../../../../../modules/payments/orderTypes"
import { StandardButton } from "../../../buttons/StandardButton"
import { IconBadge } from "../../../badges/IconBadge"
import { CreateOrderDialogWrap } from "../CreateOrderDialogWrap"
import { CreateOrderDialogTitle } from "../CreateOrderDialogTitle"
import { TakeawayCartProvider, useTakeawayCart } from "./TakeawayCartContext"
import { TakeawayFormContainer } from "./TakeawayFormContainer"
import type { TakeawayKitchensResponse } from "./takeawayTypes"
import { fetchTakeawayKitchens, TakeawayQuery } from "./takeawayApi"
import { calcItemsTotal } from "./components/takeawayCheckoutLib"
import { TakeawayKitchenSelectorDialog } from "./components/TakeawayKitchenSelectorDialog"
import { TakeawayStoreDetailsDialog } from "./components/TakeawayStoreDetailsDialog"

function selectKitchens(data: TakeawayKitchensResponse) {
    return {
        ...data,
        kitchens: data.kitchens.filter((kitchen) => kitchen.webshop?.orderType === WebshopType.TAKE_AWAY),
    }
}

const useStyles = makeStyles()((theme) => ({
    layout: {
        display: "flex",
        flexDirection: "column",
        height: "100svh",
        overflow: "hidden",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        flex: 1,
        minWidth: 0,
    },
    title: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "1rem",
        fontWeight: 400,
    },
    infoButton: {
        flexShrink: 0,
        color: theme.palette.text.primary,
        padding: ".25rem",
    },
    showOrderButton: {
        flexShrink: 0,
        whiteSpace: "nowrap",
    },
    iconSize: {
        fontSize: "1.25rem !important",
    },
}))

type Props = Readonly<{
    open: boolean
    onClose: () => void
}>

function TakeawayOrderDialogContent({ onClose }: Readonly<{ onClose: () => void }>) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const cart = useTakeawayCart()
    const [showStoreDetails, setShowStoreDetails] = useState(false)

    const allItems = Object.values(cart?.cartByDate ?? {}).flat()
    const cartTotal = calcItemsTotal(allItems)

    const selectedKitchen = cart?.selectedKitchen
    const kitchenName = selectedKitchen?.name

    return (
        <div className={classes.layout}>
            <CreateOrderDialogTitle handleClose={onClose}>
                <>
                    <Box className={classes.titleRow}>
                        {kitchenName ? (
                            <LocalizedStrict id="create-order-take-away-title-with-kitchen" vars={{ kitchenName }}>
                                <Typography className={classes.title}>Takeaway - {kitchenName}</Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="create-order-take-away-title">
                                <Typography className={classes.title}>Takeaway</Typography>
                            </LocalizedStrict>
                        )}
                        {selectedKitchen && (
                            <IconButton
                                className={classes.infoButton}
                                size="small"
                                onClick={() => setShowStoreDetails(true)}
                            >
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </IconButton>
                        )}
                    </Box>

                    {cartTotal ? (
                        <StandardButton
                            color="black"
                            fullWidth={false}
                            size="large"
                            startIcon={<FontAwesomeIcon icon={faCartShopping} className={classes.iconSize} />}
                            onClick={cart?.onShowOrder}
                            className={classes.showOrderButton}
                        >
                            <LocalizedStrict
                                id="takeaway-show-order-button"
                                vars={{ price: moneyFactory.format(cartTotal) }}
                            >
                                <span>Show order: {moneyFactory.format(cartTotal)}</span>
                            </LocalizedStrict>
                        </StandardButton>
                    ) : (
                        <IconBadge variant="font-awesome" color="blue" fontAwesomeIcon={faCartShopping} />
                    )}
                </>
            </CreateOrderDialogTitle>

            <TakeawayFormContainer onClose={onClose} />

            {showStoreDetails && selectedKitchen && (
                <TakeawayStoreDetailsDialog kitchen={selectedKitchen} onClose={() => setShowStoreDetails(false)} />
            )}
        </div>
    )
}

function TakeawayOrderDialogFlow({ open, onClose }: Props) {
    const cart = useTakeawayCart()
    const selectedKitchen = useAppSelector(selectKitchen)

    const { data: kitchensData, isInitialLoading: isLoadingKitchens } = useApiQuery(fetchTakeawayKitchens, {
        queryName: TakeawayQuery.TAKEAWAY_KITCHENS,
        dependencies: { locationKitchenId: selectedKitchen?.id },
        select: selectKitchens,
    })

    useEffect(() => {
        if (!kitchensData || !cart || cart.selectedKitchen) return
        const { kitchens } = kitchensData
        if (kitchens.length === 1) {
            cart.onKitchenSelect(kitchens[0])
        }
    }, [kitchensData, cart])

    // Show selector first
    if (!cart?.selectedKitchen) {
        if (!isLoadingKitchens && kitchensData?.kitchens.length === 1) return null

        return (
            <TakeawayKitchenSelectorDialog
                isLoading={isLoadingKitchens}
                kitchens={kitchensData?.kitchens ?? []}
                onSelect={(kitchen) => cart?.onKitchenSelect(kitchen)}
                onClose={onClose}
            />
        )
    }

    return (
        <CreateOrderDialogWrap onBehalfOf={false} open={open} onClose={onClose}>
            <TakeawayOrderDialogContent onClose={onClose} />
        </CreateOrderDialogWrap>
    )
}

export function TakeawayOrderDialog({ open, onClose }: Props) {
    return (
        <TakeawayCartProvider>
            <TakeawayOrderDialogFlow open={open} onClose={onClose} />
        </TakeawayCartProvider>
    )
}
